import { computed, makeAutoObservable } from 'mobx'
import { Split, SplitModel, SplitPercentage } from '../models/Split'
import { addSplit, deleteSplit, getSplitPercentages, loadSplits, saveSplitPercentages, updateSplit } from '../services/splitsAPI'
import { ReferenceData } from '../models/common'
import { accountStore } from './AccountStore'
import { errorStore } from './ErrorStore'
import { isGuid, showInfoToast } from '../services/shared'
import { getActiveDepartmentCode } from '../utils/ActiveDepartment';

class SplitStore {
	data: Split[] = [];
	isLoading: boolean = false;
	message: string = '';
	currentRow: SplitModel | undefined;
	showModal: boolean = false;

	constructor() {
		makeAutoObservable(this);
	}

	async loadData() {
		if (this.data.length || this.isLoading) return;
		const departmentCode = getActiveDepartmentCode();
		this.setLoading(true);
		try {
			const response = await loadSplits();
			if (departmentCode !== getActiveDepartmentCode()) {
				return;
			}

			this.setData(response || []);
		} catch (error: any) {
			this.setError(error.message);
		} finally {
			this.setLoading(false);
		}
	}

	async reloadForActiveDepartment() {
		this.reset();
		await this.loadData();
	}

	async saveSplit(onSuccess?: () => void) {
		if (!this.currentRow) return;
		this.setLoading(true);

		try {
			const nonZeroSplits = this.currentRow.splitPercentages?.filter(row => row.splitPercent > 0);
			if (!nonZeroSplits) {
				this.setMessage(`Split does not have any percentages. Cannot save`);
				return;
			}

			if (isGuid(this.currentRow.id)) {
				await saveSplitPercentages(this.currentRow.id, nonZeroSplits as SplitPercentage[]);
			} else {
				const response = await addSplit({ ...this.currentRow, splitPercentages: nonZeroSplits });

				if (response) {
					const newId = response.id;
					const newPercentages = this.currentRow.splitPercentages?.map(row => ({ ...row, splitId: newId }));
					const newSplit = { ...this.currentRow, ...response, splitPercentages: newPercentages }
					this.setCurrent((newSplit as SplitModel));
					this.add(newSplit);
				}
			}
			this.setMessage(`Split details saved successfully`);
			onSuccess?.();
		} catch (error: any) {
			this.setError(error.message);
		} finally {
			this.setLoading(false);
		}
	}

	async delete(split: Split) {
		this.setLoading(true);
		try {
			await deleteSplit(split.id);
			this.setData(this.data.filter(s => s.id !== split.id));
		} catch (error: any) {
			this.setError(error.message);
		} finally {
			this.setLoading(false);
		}
	}

	async update(split: Split) {
		this.setLoading(true);
		try {
			await updateSplit(split);

			const index = this.data.findIndex(s => s.id === split.id);
			this.data[index] = split;

			this.removeCurrent();
		} catch (error: any) {
			this.setError(error.message);
			throw error;
		} finally {
			this.setLoading(false);
		}
	}

	async LoadDetails(split: Split) {
		this.setLoading(true);
		try {
			const splitInfo = await getSplitPercentages(split.id);
			this.setCurrent(new SplitModel({ ...split, splitPercentages: splitInfo }));
		} catch (error: any) {
			this.setError(error.message);
		} finally {
			this.setLoading(false);
		}
	}

	async setupNew(name?: string) {
		this.setLoading(true);
		try {
			await accountStore.loadData();
			const accounts = accountStore.data;

			this.setCurrent(new SplitModel({
				id: '',
				name: name ?? '',
				splitPercentages: accounts
					.filter(a => a.isActive)
					.map(a => ({ accountId: a.id, accountName: a.name, isActive: a.isActive, splitPercent: 0, splitId: '' })),
				isActive: true
			}));
			this.setShowModal(true);
		} catch (error: any) {
			this.setError(error.message);
		} finally {
			this.setShowModal(true);
			this.setLoading(false);
		}
	}

	@computed
	get options() {
		return this.data as ReferenceData[];
	};

	add(split: Split) {
		this.data = [split, ...this.data];
	}

	setCurrent(split: SplitModel) {
		this.currentRow = split;
	}

	removeCurrent() {
		this.currentRow = undefined;
	}

	setLoading(loading: boolean) {
		this.isLoading = loading;
	}

	setError(error: string) {
		errorStore.report(error);
	}

	setMessage(message: string) {
		showInfoToast(message, 3000);
	}

	clearForm() {
		this.removeCurrent();
	}

	setData(data: Split[]) {
		this.data = data;
	}

	setShowModal(show: boolean) {
		this.showModal = show;
	}

	reset() {
		this.data = [];
		this.isLoading = false;
		this.showModal = false;
		this.removeCurrent();
	}
}

export const splitStore = new SplitStore()
